import React, { FocusEvent, forwardRef, Fragment, useContext, useEffect } from 'react';
import {
	convertPlatesToArray,
	getInputFields,
	getRegexMatchedPattern,
	ICountry,
	removeAllWhitespaces,
	IPlatesValidatedState,
	OfferContext,
	getLabelValue,
	ITypeLicensePlateFields,
} from '@adac-mp/shared';
import { Input, Dropdown, ErrorCrossIcon, WarningInfo, LicensePlateTag, Typography } from '../../index';
import { useViewport } from '../../hooks/useViewport';
import { trackInteractionClick } from '../../utils/tracking/tracking';

export interface IPlatesInputProps {
	'data-cy'?: string;
	textMessage?: string;
	id?: string;
	border?: boolean;
	hasSpecialPlates?: boolean;
	disabled?: boolean;
	hasError?: boolean;
	countries: ICountry[];
	plates: IPlatesValidatedState;
	confirmedError?: boolean;
	withAutoFocus?: boolean;
	insideOfModal?: boolean;
	type?: string;
	country: ICountry | undefined;
	licensePlatesLabels?: ITypeLicensePlateFields;
	onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
	onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
	onChange: (value: IPlatesValidatedState) => void;
	setCountry: (country: ICountry | undefined) => void;
	setHideModal?: (value: boolean) => void;
}

type Ref = HTMLInputElement[];

// eslint-disable-next-line prefer-arrow-callback
export const PlatesInput = forwardRef<Ref, IPlatesInputProps>((props, ref) => {
	const {
		id,
		textMessage = undefined,
		border = false,
		disabled = false,
		withAutoFocus = false,
		insideOfModal = false,
		countries,
		plates,
		confirmedError,
		licensePlatesLabels,
		type,
		country,
		onChange,
		onBlur = () => {},
		onFocus = () => {},
		setCountry,
		'data-cy': dataCy = 'plates-part',
	} = props;
	const { labels } = useContext(OfferContext);
	const { viewport } = useViewport();

	useEffect(() => {
		if (plates.value && typeof plates.value === 'string' && country) {
			const platesAsArray = convertPlatesToArray(plates.value, country.isoCode, plates.specialPlates);
			onChange({ ...plates, value: platesAsArray });
		}
	}, []);

	const inputFields = country?.isoCode ? getInputFields(country.isoCode, plates.specialPlates) : [];

	const renderDivider = () => (
		<div
			style={{
				background:
					'linear-gradient(180deg, rgba(214, 214, 214, 0) 0%, #d6d6d6 49.23%, rgba(214, 214, 214, 0) 100%)',
			}}
			className="w-px h-5 shrink-0"
		/>
	);

	const getInputDivider = (index: number) => {
		if (index >= inputFields.length - 1) {
			return <></>;
		}
		return renderDivider();
	};

	const getErrorText = () => {
		if (confirmedError) {
			return (
				licensePlatesLabels?.errorConfirmLicenseplate ||
				'Die Kennzeichen stimmen nicht überein. Bitte überprüfen Sie Ihre Eingabe.'
			);
		}

		return plates.specialPlates
			? licensePlatesLabels?.errorSpecialLicenseplate || 'Bitte geben Sie ein gültiges Sonderkennzeichen ein.'
			: `${licensePlatesLabels?.errorLicenseplate || 'Bitte geben Sie ein vollständiges Kennzeichen ein, z.B.'} ${
					country?.entryhint ? ` ${removeAllWhitespaces(country.entryhint)}.` : '.'
			  }`;
	};

	const renderHelperText = (Icon: React.ReactNode, text?: string) => {
		return (
			<div className="flex gap-2 pl-3 pt-1.5">
				{Icon}
				<Typography data-cy="helperText">{text}</Typography>
			</div>
		);
	};

	const updatePlates = (index, val) => {
		if (country) {
			const newArray = plates.value ? [...plates.value] : [];
			newArray[index] = val;
			const regularExpression = plates.specialPlates
				? country.regexSpecialLicensplate
				: country.regexlicenseplate;
			const regex = new RegExp(regularExpression).exec(newArray.join(''));
			const arrayHasEmptyValues = newArray.some((value) => value === '');
			onChange({ ...plates, value: newArray, error: !regex || !val || arrayHasEmptyValues });
		}
	};

	const renderLicensePlateIcon = () =>
		country &&
		country.isoCode !== 'XX' && (
			<LicensePlateTag
				size={viewport === 'desktop' ? 52 : 44}
				countryCode={country.licenseplateCountryCode}
				isEu={country.euFlag}
			/>
		);

	const getPlatesEntryHint = (index: number) => {
		if (!country || !country.entryhint) {
			return '';
		}

		const { entryhint, isoCode } = country;

		// Removing last - of entryHint for special plates (e.g. M-UC-123 --> M-UC123)
		if (plates.specialPlates) {
			const indexToBeRemoved = entryhint.lastIndexOf('-');
			const formattedEntryhint = entryhint.split('');
			formattedEntryhint[indexToBeRemoved] = '';
			return removeAllWhitespaces(isoCode === 'DE' ? formattedEntryhint.join('') : entryhint);
		}

		return removeAllWhitespaces(isoCode === 'DE' ? entryhint.split('-')[index] : entryhint);
	};

	return (
		<>
			{textMessage && <p className="mb-4 px-4 after:content-['_*'] after:text-error">{textMessage}</p>}
			<div
				id={id}
				className={`flex items-center bg-white px-1 rounded 
				${
					!disabled &&
					'[&:not(:focus-within)]:hover:shadow-innerHover active:shadow-innerWarning focus-within:shadow-innerWarning'
				}
				${border && 'border border-solid rounded border-gray-600'} ${
					plates?.error || confirmedError ? '!shadow-innerError' : ''
				} 
				${plates?.warning ? 'shadow-innerWarning' : ''}`}>
				{countries.length > 1 ? (
					<>
						<Dropdown
							ref={ref}
							key={`${dataCy} dropdown: land`}
							showLabel
							hasPortal={!insideOfModal}
							disabled={disabled}
							icon={renderLicensePlateIcon()}
							id={`${dataCy} dropdown: land`}
							placeholder={getLabelValue('mp.country', labels) || 'Land'}
							data-cy={`${dataCy} dropdown: land`}
							items={countries.map(({ isoCode, name }) => ({
								value: viewport === 'desktop' ? name : isoCode,
								label: `${name} (${isoCode})`,
							}))}
							value={viewport === 'desktop' ? country?.name : country?.isoCode}
							setValue={(option) => {
								if (viewport === 'mobile' && country?.isoCode !== option) {
									const currentCountry = countries.find((c: ICountry) => c.isoCode === option);
									setCountry(currentCountry);
								}
								if (viewport === 'desktop' && country?.name !== option) {
									const currentCountry = countries.find((c: ICountry) => c.name === option);
									setCountry(currentCountry);
								}
								if (country?.name !== option) {
									onChange({ ...plates, value: [''], error: false });
								}
							}}
							className="border-none rounded"
							contentClassName="max-w-[180px] min-w-[153px] md:min-w-[294px] md:max-w-auto -left-1"
							contentStyle={{ maxHeight: 360 }}
							iconStyle={{ padding: '0', left: '1px', bottom: '4px' }}
							arrowStyle={{ marginLeft: '16px' }}
							style={
								viewport === 'desktop'
									? { marginBottom: 0, paddingLeft: '60px' }
									: { marginBottom: 0, paddingLeft: '40px', paddingRight: '10px' }
							}
						/>
						{renderDivider()}
					</>
				) : (
					<div>{renderLicensePlateIcon()}</div>
				)}
				{inputFields?.map(({ restrictions: { inputRegex, maxLength } }, index) => (
					<Fragment key={index}>
						<form
							className="flex-grow"
							autoComplete="off"
							onSubmit={(e) => {
								e.preventDefault();
								(document.activeElement as HTMLElement).blur();
							}}>
							<Input
								ref={ref}
								key={`${dataCy}-${index}`}
								basic
								id={`${dataCy}-${index}`}
								withAutoFocus={withAutoFocus && index === 0}
								disabled={disabled}
								maxLength={maxLength ?? 0}
								data-cy={`${dataCy}-${index}`}
								className={`my-0.5 border-l-0 border-r-0 border-b-0 border-t-0 ${
									index === 0 && 'pl-4'
								}`}
								style={viewport === 'mobile' ? { paddingRight: '0' } : undefined}
								value={plates?.value ? plates?.value[index] : ''}
								type={type}
								placeholder={getPlatesEntryHint(index)}
								autocomplete="off"
								onChange={(e) => {
									if (disabled) {
										return;
									}
									const val = removeAllWhitespaces(e.target.value.toUpperCase());
									const matchedPattern = getRegexMatchedPattern(inputRegex, val) ?? '';
									if (inputRegex && matchedPattern !== val) {
										return;
									}
									updatePlates(index, val);
								}}
								onBlur={(ev) => {
									if (confirmedError) {
										trackInteractionClick({
											label: 'Kennzeicheneingabe fehlgeschlagen',
											element: 'Kennzeicheneingabe',
										});
									}
									onBlur(ev);
								}}
								onFocus={onFocus}
								onPaste={(e) => e.preventDefault()}
							/>
						</form>
						{inputFields.length > 1 && getInputDivider(index)}
					</Fragment>
				))}
			</div>
			{(plates?.error || confirmedError) && renderHelperText(<ErrorCrossIcon />, getErrorText())}
			{plates?.warning && renderHelperText(<WarningInfo />, 'Kennzeichen wurden nicht erkannt')}
		</>
	);
});

PlatesInput.displayName = 'PlatesInput';
